import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { DownloadIcon, FileTextIcon, XIcon } from 'lucide-react';
import { ConceptPaper } from './ConceptPaper';
const PDF_TITLE = 'Snakker — Concept Paper';

/** Switches the page to A4 portrait and prints only the concept paper. */
function printConceptPaper() {
  const style = document.createElement('style');
  style.setAttribute('data-paper-print', 'true');
  style.textContent = '@media print { @page { size: A4 portrait; margin: 14mm 14mm 16mm; } }';
  document.head.appendChild(style);
  const originalTitle = document.title;
  document.body.classList.add('printing-paper');
  document.title = PDF_TITLE;
  const cleanup = () => {
    document.body.classList.remove('printing-paper');
    document.title = originalTitle;
    style.remove();
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  window.setTimeout(() => window.print(), 400);
}
export function ConceptPaperButton() {
  const [isOpen, setIsOpen] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
      // Keep deck arrow navigation from firing behind the dialog.
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft' || event.key === ' ') {
        event.stopPropagation();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    closeButtonRef.current?.focus();
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);
  return <>
      <button type="button" onClick={() => setIsOpen(true)} aria-haspopup="dialog" aria-expanded={isOpen} className="hide-on-print flex h-8 items-center gap-1.5 rounded-full border border-black/10 bg-white/90 px-3 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white" data-id="element-91" data-mp="2r3nwy1rl9v8v">
        <FileTextIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-92" data-mp="kqhlgnwvefs3" />
        <span className="text-xs font-semibold" data-id="element-93" data-mp="yphe06ni7etj">Concept paper</span>
      </button>

      {createPortal(<AnimatePresence data-id="element-94" data-mp="tqnyk8onpdqq">
          {isOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="paper-overlay fixed inset-0 z-[100] flex flex-col bg-black/60 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="concept-paper-title" data-id="element-95" data-mp="totd8t9h3jm4">
            <header className="paper-hide-on-print flex flex-shrink-0 items-center justify-between gap-4 border-b border-black/10 bg-white px-[clamp(1rem,2vw,1.75rem)] py-3" data-id="element-96" data-mp="1chffpwevy8fo">
              <div className="flex items-center gap-3" data-id="element-97" data-mp="1mfuxh8s0w5ci">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-98" data-mp="1md8px954tpua">
                  <FileTextIcon className="h-5 w-5" aria-hidden="true" data-id="element-99" data-mp="2a2yquydb15y0" />
                </span>
                <div data-id="element-100" data-mp="1w377sj7xkzd5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--muted)]" data-id="element-101" data-mp="1w1meewxcrmgv">
                    Executive summary
                  </p>
                  <h2 id="concept-paper-title" className="text-base font-extrabold text-[var(--fg)]" data-id="element-102" data-mp="1md4h4poa7xhz">
                    The Snak concept paper
                  </h2>
                </div>
              </div>

              <div className="flex items-center gap-2" data-id="element-103" data-mp="1de0tr9i30vxo">
                <button type="button" onClick={printConceptPaper} className="flex h-10 items-center gap-2 rounded-full bg-[var(--brand)] px-4 text-xs font-extrabold text-white shadow-sm transition hover:bg-[var(--brand-light)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--brand)]/30" data-id="element-104" data-mp="1iahncj81w2i5">
                  <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-105" data-mp="26oe4y9c43zcs" />
                  Download PDF
                </button>
                <button ref={closeButtonRef} type="button" onClick={() => setIsOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-full text-[var(--muted)] transition hover:bg-[var(--surface)] hover:text-[var(--fg)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--brand)]/20" aria-label="Close concept paper" data-id="element-106" data-mp="21wwe4e3szf5j">
                  <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-107" data-mp="1e0s1d12vwupx" />
                </button>
              </div>
            </header>

            <div className="paper-scroll min-h-0 flex-1 overflow-y-auto bg-[var(--surface)] px-[clamp(0.75rem,2vw,2rem)] py-[clamp(1rem,3vw,2.5rem)]" data-id="element-108" data-mp="3dc1jjpvbjrsu">
              <ConceptPaper data-id="element-109" data-mp="3fxsfpgiryp43" />
            </div>
          </motion.div>}
        </AnimatePresence>, document.body)}
    </>;
}